import { Project } from '@shared/types';

/**
 * Get the display text for a project mention
 */
export const getProjectMentionText = (project: Project): string => {
  // Use project name if available, otherwise fall back to code
  const { name, code } = project.projectInfo || {};

  return name?.trim() || code || '';
};

/**
 * Normalize query by removing # symbol, converting to lowercase and trimming
 */
export const normalizeProjectQuery = (query: string): string => {
  return query.replace(/^#/, '').toLowerCase().trim();
};

/**
 * Find exact project code match
 */
export const findExactProjectMatch = <T extends Project>(
  projects: T[],
  query: string,
): T | null => {
  if (!query || projects.length === 0) return null;

  const normalizedQuery = normalizeProjectQuery(query);

  return (
    projects.find(
      (project) => project.projectInfo.code?.toLowerCase() === normalizedQuery,
    ) || null
  );
};

/**
 * Replace partial project mention with full mention text and clean up duplicate spaces
 *
 * Examples:
 * - "View #proj details" → "View #project-alpha details"
 * - "#proj" → "#project-alpha "
 */
export const handleProjectMentionSelection = (
  mentionText: string,
  replaceStart: number,
  replaceEnd: number,
  search: string,
  setSearch: (value: string) => void,
  setCursorPosition: (position: number) => void,
  closeMention: () => void,
  onInputChange?: (searchQuery: string) => void,
): void => {
  // Replace partial mention with full mention + space
  const newValue =
    search.substring(0, replaceStart) +
    mentionText +
    ' ' +
    search.substring(replaceEnd);

  // Clean up duplicate spaces
  const cleanedValue = newValue.replace(/\s{2,}/g, ' ');

  // Position cursor after mention + space
  const newCursorPosition = replaceStart + mentionText.length + 1;

  setSearch(cleanedValue);
  setCursorPosition(newCursorPosition);
  closeMention();
  onInputChange?.(cleanedValue);
};
